import { DesktopAppFrame } from "@/components/desktop/DesktopChrome";
import { BrandColors } from "@/constants/theme";
import { useAuth } from "@/contexts/auth-context";
import { useColorScheme } from "@/hooks/use-color-scheme";
import { useDesktopLayout } from "@/hooks/use-desktop-layout";
import Ionicons from "@react-native-vector-icons/ionicons";
import { useRouter } from "expo-router";
import React from "react";
import {
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import {
  SafeAreaView,
  useSafeAreaInsets,
} from "react-native-safe-area-context";
import { ThemedText } from "../themed-text";

interface CreateRequestLayoutProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  onBack?: () => void;
  onNext?: () => void;
  nextLabel?: string;
  isNextDisabled?: boolean;
  isNextLoading?: boolean;
  step: number;
  totalSteps?: number;
  scrollRef?: React.RefObject<ScrollView | null>;
}

export function CreateRequestLayout({
  title,
  subtitle,
  children,
  onBack,
  onNext,
  nextLabel = "Next",
  isNextDisabled = false,
  isNextLoading = false,
  step,
  totalSteps = 5,
  scrollRef,
}: CreateRequestLayoutProps) {
  const router = useRouter();
  const { theme } = useAuth();
  const insets = useSafeAreaInsets();
  const desktop = useDesktopLayout();
  const isDark = useColorScheme() === "dark";

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      router.back();
    }
  };

  const nextButton = (
    <TouchableOpacity
      testID="create-request-next"
      style={[
        styles.button,
        desktop && styles.desktopButton,
        isNextDisabled && styles.buttonDisabled,
      ]}
      onPress={onNext}
      disabled={isNextDisabled || isNextLoading}
    >
      <ThemedText style={styles.buttonText}>
        {isNextLoading ? "Loading..." : nextLabel}
      </ThemedText>
      {!isNextLoading && (
        <Ionicons name="chevron-forward" size={20} color="#fff" />
      )}
    </TouchableOpacity>
  );

  if (desktop) {
    const pageColor = isDark ? theme.background : BrandColors.backgroundGray;

    return (
      <DesktopAppFrame>
        <SafeAreaView
          style={[styles.container, { backgroundColor: pageColor }]}
          edges={[]}
        >
          <KeyboardAvoidingView
            behavior={Platform.OS === "ios" ? "padding" : "height"}
            style={{ flex: 1 }}
          >
            <ScrollView
              ref={scrollRef}
              contentContainerStyle={styles.desktopScroll}
              showsVerticalScrollIndicator={false}
              keyboardShouldPersistTaps="handled"
            >
              <View style={styles.desktopHeader}>
                <TouchableOpacity
                  onPress={handleBack}
                  style={[styles.backButton, { backgroundColor: theme.card }]}
                >
                  <Ionicons name="chevron-back" size={24} color={theme.text} />
                </TouchableOpacity>
                <View style={styles.desktopTitle}>
                  <ThemedText type="title" style={styles.title}>
                    {title}
                  </ThemedText>
                  {subtitle ? (
                    <ThemedText style={styles.subtitle}>{subtitle}</ThemedText>
                  ) : null}
                </View>
                <View style={styles.stepper}>
                  {Array.from({ length: totalSteps }, (_, index) => {
                    const number = index + 1;
                    const reached = number <= step;
                    return (
                      <View key={number} style={styles.stepItem}>
                        <View
                          style={[
                            styles.stepCircle,
                            {
                              backgroundColor: reached
                                ? BrandColors.primary
                                : theme.border,
                            },
                          ]}
                        >
                          <Text
                            style={{
                              color: reached ? "#fff" : theme.icon,
                              fontWeight: "700",
                              fontSize: 13,
                            }}
                          >
                            {number}
                          </Text>
                        </View>
                        {number < totalSteps ? (
                          <View
                            style={[
                              styles.stepLine,
                              {
                                backgroundColor:
                                  number < step
                                    ? BrandColors.primary
                                    : theme.border,
                              },
                            ]}
                          />
                        ) : null}
                      </View>
                    );
                  })}
                </View>
              </View>
              <View
                style={[
                  styles.desktopCard,
                  { backgroundColor: theme.card, borderColor: theme.border },
                ]}
              >
                {children}
                <View style={styles.desktopActions}>{nextButton}</View>
              </View>
            </ScrollView>
          </KeyboardAvoidingView>
        </SafeAreaView>
      </DesktopAppFrame>
    );
  }

  return (
    <SafeAreaView
      style={[styles.container, { backgroundColor: theme.background }]}
      edges={["top"]}
    >
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        style={{ flex: 1 }}
        keyboardVerticalOffset={Platform.OS === "ios" ? 0 : 0}
      >
        <View style={styles.header}>
          <TouchableOpacity
            onPress={handleBack}
            style={[styles.backButton, { backgroundColor: theme.border }]}
          >
            <Ionicons name="chevron-back" size={24} color={theme.text} />
          </TouchableOpacity>
          <View style={styles.progressContainer}>
            <ThemedText style={styles.stepText}>
              Step {step} of {totalSteps}
            </ThemedText>
            <View
              style={[styles.progressBar, { backgroundColor: theme.border }]}
            >
              <View
                style={[
                  styles.progressFill,
                  { width: `${(step / totalSteps) * 100}%` },
                ]}
              />
            </View>
          </View>
        </View>

        <ScrollView
          ref={scrollRef}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
          automaticallyAdjustKeyboardInsets={true}
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode="interactive"
        >
          <View style={styles.titleSection}>
            <ThemedText type="title" style={styles.title}>
              {title}
            </ThemedText>
            {subtitle && (
              <ThemedText style={styles.subtitle}>{subtitle}</ThemedText>
            )}
          </View>

          {children}
        </ScrollView>
      </KeyboardAvoidingView>
      <View
        style={[
          styles.footer,
          {
            backgroundColor: theme.background,
            paddingBottom: insets.bottom + 20,
          },
        ]}
      >
        {nextButton}
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 20,
    paddingVertical: 16,
    gap: 16,
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: "center",
    alignItems: "center",
  },
  progressContainer: {
    width: "50%",
    gap: 8,
  },
  stepText: {
    color: "#94a3b8",
    textAlign: "right",
    fontSize: 12,
    fontWeight: "600",
  },
  progressBar: {
    height: 6,
    backgroundColor: "#1e293b",
    borderRadius: 3,
    overflow: "hidden",
  },
  progressFill: {
    height: "100%",
    backgroundColor: BrandColors.primary,
  },
  scrollContent: {
    paddingHorizontal: 20,
    paddingBottom: 40,
    paddingTop: 20,
  },
  titleSection: {
    marginBottom: 24,
  },
  title: {
    fontSize: 24,
  },
  subtitle: {
    fontSize: 14,
    marginTop: 8,
    lineHeight: 20,
  },
  footer: {
    paddingHorizontal: 20,
    paddingTop: 20,
  },
  button: {
    backgroundColor: BrandColors.primary,
    height: 56,
    borderRadius: 16,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  buttonDisabled: {
    opacity: 0.5,
  },
  buttonText: {
    color: "#fff",
    fontSize: 16,
    fontWeight: "600",
  },
  desktopScroll: {
    padding: 32,
    gap: 24,
  },
  desktopHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
  },
  desktopTitle: {
    flex: 1,
  },
  stepper: {
    flexDirection: "row",
    alignItems: "center",
  },
  stepItem: {
    flexDirection: "row",
    alignItems: "center",
  },
  stepCircle: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: "center",
    justifyContent: "center",
  },
  stepLine: {
    width: 24,
    height: 2,
    marginHorizontal: 4,
  },
  desktopCard: {
    borderWidth: 1,
    borderRadius: 20,
    padding: 24,
    gap: 16,
  },
  desktopActions: {
    alignItems: "flex-end",
    marginTop: 8,
  },
  desktopButton: {
    minWidth: 180,
    paddingHorizontal: 24,
  },
});
